import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  Heading,
  Text,
  useMediaQuery,
} from "@rshbintech.dboul/core-ui";
import { AuthService } from "@rshbintech.dboul/http";
import { FailedSendRequest as FailedSendRequestImage } from "@rshbintech.dboul/images";
import { useTranslation } from "@rshbintech.dboul/utils";
import { checkIsLoginPage } from "../utils";
import { AppLocale } from "../../common/types";
import { AuthNameSpaceType } from "../types";
import { LogoutPageProps } from "./types";
import { useTimer } from "./utils";
import styles from "./styles.scss";

export const LogoutPage = ({ dataTestId = "logout-page" }: LogoutPageProps) => {
  const { translate } = useTranslation<AppLocale, AuthNameSpaceType>("LOGOUT");
  const [openedLogoutPage, setOpenedLogoutPage] = useState(false);
  const [hasProblemWithPermissions, setHasProblemWithPermissions] =
    useState(false);

  const isMobile = useMediaQuery("mobile");

  const { countDown } = useTimer(openedLogoutPage, 30);

  const isLoginPage = checkIsLoginPage();

  const handleClose = useCallback(() => {
    /**
     * INFO: первое условие выполняется в том случае, если проходим
     * аутентификацию и прочие процессы логин зоны через монолит и на каком-либо
     * запросе после ввода логина и пароля ловим 401 либо вызываем логаут монолита.
     * В этот момент мы уже находимся на /login, поэтому нужен рефреш страницы.
     */
    if (isLoginPage) {
      window.location.reload();
    } else {
      AuthService.redirectToLoginPage();
    }
  }, [isLoginPage]);

  useEffect(() => {
    AuthService.onUserLogout(() => {
      setHasProblemWithPermissions(AuthService.hasNoPermissions);
      setOpenedLogoutPage(true);
    });
  }, []);

  useEffect(() => {
    if (countDown === 0) {
      handleClose();
    }
  }, [countDown, handleClose]);

  return (
    <Dialog
      fullscreen
      opened={openedLogoutPage}
      onClose={handleClose}
      className={styles.dialog}
      dataTestId={dataTestId}
    >
      <div className={styles.wrapper}>
        <FailedSendRequestImage
          height={isMobile ? 96 : 240}
          width={isMobile ? 96 : 240}
          dataTestId={`${dataTestId}-image`}
        />
        <Heading
          level={2}
          className={styles.heading}
          dataTestId={`${dataTestId}-heading`}
        >
          {`${translate("END_SESSION_TEXT")}\n${
            hasProblemWithPermissions ? "" : translate("FOR_CONTINUE_TEXT")
          }`}
        </Heading>
        {hasProblemWithPermissions && (
          <Heading
            level={4}
            className={styles["no-permissions"]}
            dataTestId={`${dataTestId}-no-permissions`}
          >
            {translate("NO_PERMISSIONS_TEXT")}
          </Heading>
        )}
        <Text
          size="medium"
          className={styles.hint}
          dataTestId={`${dataTestId}-hint`}
        >
          {translate("HINT_TEXT")}
        </Text>
        <Text
          size="medium"
          className={styles.timer}
          dataTestId={`${dataTestId}-timer`}
        >
          {countDown} {translate("SECONDS")}
        </Text>
        <Button
          htmlType="button"
          onClick={handleClose}
          className={styles.button}
          dataTestId={`${dataTestId}-button`}
        >
          {translate("CLOSE")}
        </Button>
      </div>
    </Dialog>
  );
};
